import { X } from 'lucide-react';
import { type ChangeEvent, useRef } from 'react';
import { cn } from '@/lib/utils';
import Button from '@/shared/components/ui/Button';

export interface PhotoItem {
  id: string;
  url: string;
  file?: File;
}

interface PhotoUploaderProps {
  photos: PhotoItem[];
  onAdd: (files: File[]) => void;
  onRemove: (id: string) => void;
  label?: string;
  maxPhotos?: number;
  accept?: string;
  disabled?: boolean;
  className?: string;
}

const PhotoUploader = ({
  photos,
  onAdd,
  onRemove,
  label = 'FOTO/MEDIA',
  maxPhotos = 35,
  accept = 'image/*',
  disabled = false,
  className,
}: PhotoUploaderProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const remaining = maxPhotos - photos.length;
  const isFull = remaining <= 0;

  const openPicker = () => {
    if (disabled || isFull) return;
    inputRef.current?.click();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) {
      onAdd(files.slice(0, Math.max(remaining, 0)));
    }
    e.target.value = '';
  };

  return (
    <div className={cn('flex w-full flex-col gap-y-3', className)}>
      <p className="text-[clamp(14px,.9vw,16px)] text-text-grey uppercase dark:text-white">
        {label}
      </p>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        className="hidden!"
        onChange={handleChange}
        disabled={disabled || isFull}
      />

      {photos.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {photos.map((photo) => (
            <div key={photo.id} className="relative h-[72px] w-[72px] shrink-0">
              <img
                src={photo.url}
                alt=""
                className="h-full w-full rounded-lg border border-blue-cs-200 object-cover"
              />
              <button
                type="button"
                aria-label="Hapus foto"
                onClick={() => onRemove(photo.id)}
                disabled={disabled}
                className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#e31616] text-white shadow transition hover:opacity-80 disabled:opacity-50"
              >
                <X size={12} strokeWidth={3} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          customButton="btn-fill-blue rounded-[8px]!"
          onClick={openPicker}
          disabled={disabled || isFull}
        >
          Tambahkan Foto
        </Button>
        <span className="body1 text-text-grey">
          Maksimal {maxPhotos} Foto
          {photos.length > 0 && ` (${photos.length}/${maxPhotos})`}
        </span>
      </div>
    </div>
  );
};

export default PhotoUploader;
